import {
  FC,
  useEffect,
  useState,
  useCallback,
  useRef,
  DragEvent,
  MouseEvent as ReactMouseEvent,
  CSSProperties,
} from 'react'
import { notification } from 'antd'
import { useToggle } from 'react-use'
import { useNavigate } from 'react-router-dom'
import {
  EditOutlined,
  PlusOutlined,
  MenuOutlined,
} from '@ant-design/icons'
import { MyCourse, MyCourseChapter, MyCourseChapterTheme } from '@models/myCourse'
import { CourseThemeDetailed } from '@models/courseTheme'
import { AccordionBodyProps } from '@molecules/Accordion'
import StyledButton from '@organs/StyledButton'
import DoneButton from '@organisms/DoneButton'
import CloseButton from '@organisms/CloseButton'
import AddButton from '@organisms/AddButton'
import Link, { LinkStyle } from '@organisms/Link'
import Modal from '@organisms/Modal'
import ButtonStyle from '@enums/ButtonStyle'
import { Routes } from '@enums/Routes'
import DeleteButton from '@templates/DeleteButton'
import ChapterFormModal from '@modals/ChapterFormModal'
import ThemeFormModal from '@modals/ThemeFormModal'
import ManualFinalTestModal from '@modals/ManualFinalTestModal'
import ScormFinalTestModal from '@modals/ScormFinalTestModal'
import XlsFinalTestModal from '@modals/XlsFinalTestModal'
import {
  usePublishMyCourseMutation,
  useDeleteMyCourseMutation,
  useRestoreMyCourseMutation,
} from '@services/myCourses'
import { usePatchCourseFinalTestMutation } from '@services/courses'
import {
  useDeleteCourseChapterMutation,
  useReorderCourseChaptersMutation,
} from '@services/courseChapters'
import {
  useDeleteCourseThemeMutation,
  useReorderCourseChapterThemesMutation,
  useLazyGetCourseThemeByIdQuery,
} from '@services/courseThemes'
import getThemeRoute from '@utils/getThemeRoute'
import useMe from '@hooks/useMe'
import useSearchParam from '@hooks/useSearchParam'
import Onboarding from '@templates/Onboarding'
import OnboardingCodes from '@enums/OnboardingCodes'
import { DEFAULT_ANIMATION_DURATION } from '@const/animation'
import Loader from '@atoms/Loader'
import AddFinalTestButton from './AddFinalTestButton'
import { ONBOARDING_TITLE, getOnboardingSteps } from './onboarding'
import {
  BodyWrapper,
  SectionTitle,
  SectionHeader,
  ChapterWrapper,
  ChapterHeader,
  ChapterLeftContent,
  ChapterTitle,
  ChapterActions,
  ChapterArrow,
  ThemesWrapper,
  ThemesTitle,
  ChapterDragHandle,
  AddThemeButton,
  ThemeItem,
  ThemeDragHandle,
  ThemeTitle,
  ThemeTitleGroup,
  ThemeActions,
  ActionsWrapper,
  NoChaptersMessage,
  FinalTestRow,
  FinalTestLeftContent,
  FinalTestTitleGroup,
  BodyLoadingOverlay,
} from './styled'

type Props = {
  course: MyCourse
  onEdit?: () => void
  isEditable?: boolean
  showDeleted?: boolean
  isExpandedLoading?: boolean
} & AccordionBodyProps

const SUCCESS_DELETE_MESSAGE = 'Удаление прошло успешно'
const ERROR_DELETE_MESSAGE = 'Произошла ошибка при удалении'
const MANUAL_FINAL_TEST_MODAL_FORM_WIDTH = 'min(1380px, calc(100vw - 40px))'
const MANUAL_FINAL_TEST_MODAL_SUCCESS_WIDTH = 'max-content'
const MANUAL_FINAL_TEST_MODAL_CONTENT_STYLE: CSSProperties = {
  borderRadius: 24,
  maxWidth: 'calc(100vw - 40px)',
  padding: '47px 47px 47px',
  boxShadow: '0 16px 32px rgba(29, 78, 137, 0.35)',
}
const COURSE_PREVIEW_ANCHOR_PREFIX = 'my-course'
const COURSE_PREVIEW_ANCHOR = (id: number) =>
  `${COURSE_PREVIEW_ANCHOR_PREFIX}-${id}`
const COURSE_PREVIEW_THEME_ANCHOR = (courseId: number, themeId: number) =>
  `${COURSE_PREVIEW_ANCHOR_PREFIX}-${courseId}-theme-${themeId}`
const COURSE_PREVIEW_FINAL_TEST_ANCHOR = (courseId: number) =>
  `${COURSE_PREVIEW_ANCHOR_PREFIX}-${courseId}-final-test`
const PREVIEW_SCROLL_DELAY = DEFAULT_ANIMATION_DURATION + 350

type ManualFinalTestModalProps = {
  width: string
  contentStyle: CSSProperties
}

const getManualFinalTestModalProps = (
  isSuccessVisible: boolean
): ManualFinalTestModalProps => {
  if (isSuccessVisible) {
    return {
      width: MANUAL_FINAL_TEST_MODAL_SUCCESS_WIDTH,
      contentStyle: {
        ...MANUAL_FINAL_TEST_MODAL_CONTENT_STYLE,
        boxSizing: 'border-box',
        minWidth: 'auto',
      },
    }
  }

  return {
    width: MANUAL_FINAL_TEST_MODAL_FORM_WIDTH,
    contentStyle: MANUAL_FINAL_TEST_MODAL_CONTENT_STYLE,
  }
}

/* eslint-disable sonarjs/cognitive-complexity */
const Body: FC<Props> = ({
  course,
  onEdit,
  isEditable = true,
  showDeleted = false,
  isExpandedLoading = false,
  onChangeSize,
  toggleLoading,
}) => {
  const {
    isCompanyGroupCurator,
    isCompanyCurator,
    isFilialCurator,
  } = useMe()
  const navigate = useNavigate()
  const canSeeOnboarding =
    isCompanyGroupCurator || isCompanyCurator || isFilialCurator
  const [expandedChapterId, setExpandedChapterId] = useState<number | null>(null)
  const [localChapters, setLocalChapters] = useState<MyCourseChapter[]>([])
  const [addChapterVisible, toggleAddChapter] = useToggle(false)
  const [editChapterData, setEditChapterData] = useState<MyCourseChapter | null>(null)

  // Theme state
  const [addThemeChapterId, setAddThemeChapterId] = useState<number | null>(null)
  const [editThemeData, setEditThemeData] = useState<{ chapterId: number } | null>(null)
  const [themeDetailForEdit, setThemeDetailForEdit] = useState<CourseThemeDetailed | null>(
    null
  )
  const [draggedThemeIndex, setDraggedThemeIndex] = useState<{ chapterId: number; index: number } | null>(null)
  const [draggedChapterIndex, setDraggedChapterIndex] = useState<number | null>(null)
  const [localThemes, setLocalThemes] = useState<Map<number, MyCourseChapterTheme[]>>(new Map())

  const [deletingChapterId, setDeletingChapterId] = useState<number | null>(null)
  const [deletingThemeId, setDeletingThemeId] = useState<number | null>(null)
  const [isDeletingFinalTest, setIsDeletingFinalTest] = useState(false)
  const [finalTestForEdit, setFinalTestForEdit] = useState<CourseThemeDetailed | null>(null)
  const hasFinalTest = Boolean(course.finalTest)
  const [isManualFinalTestVisible, toggleManualFinalTestVisible] = useToggle(false)
  const [isManualFinalTestSuccessVisible, setManualFinalTestSuccessVisible] = useState(false)
  const [isXlsFinalTestVisible, toggleXlsFinalTestVisible] = useToggle(false)
  const [isScormFinalTestVisible, toggleScormFinalTestVisible] = useToggle(false)
  const finalTest = course.finalTest
  const finalTestSource = finalTestForEdit?.source || finalTest?.source
  const manualFinalTestModalProps = getManualFinalTestModalProps(
    isManualFinalTestSuccessVisible
  )

  const [publishCourse, { isLoading: isPublishing }] = usePublishMyCourseMutation()
  const [deleteCourse, deleteState] = useDeleteMyCourseMutation()
  const [restoreCourse, restoreState] = useRestoreMyCourseMutation()
  const [deleteChapter, deleteChapterState] = useDeleteCourseChapterMutation()
  const [deleteTheme, deleteThemeState] = useDeleteCourseThemeMutation()
  const [patchCourseFinalTest, patchCourseFinalTestState] = usePatchCourseFinalTestMutation()
  const [reorderThemes] = useReorderCourseChapterThemesMutation()
  const [reorderChapters] = useReorderCourseChaptersMutation()
  const [getCourseTheme] = useLazyGetCourseThemeByIdQuery()
  const [getFinalTestTheme] = useLazyGetCourseThemeByIdQuery()
  const previewThemeId = useSearchParam('previewThemeId')
  const hasRestoredPreviewState = useRef(false)

  /** Псевдо-click после DnD тем перехватываем в capture (DnD разделов отключён — иначе dragend темы всплывает к обёртке раздела). */
  const ghostClickListenerRef = useRef<((evt: MouseEvent) => void) | null>(null)

  const disarmGhostClickAfterDrag = useCallback(() => {
    if (ghostClickListenerRef.current) {
      document.removeEventListener('click', ghostClickListenerRef.current, true)
      ghostClickListenerRef.current = null
    }
  }, [])

  const armGhostClickAfterDrag = useCallback(() => {
    disarmGhostClickAfterDrag()
    const handler = (evt: MouseEvent) => {
      evt.preventDefault()
      evt.stopImmediatePropagation()
      ghostClickListenerRef.current = null
    }
    ghostClickListenerRef.current = handler
    document.addEventListener('click', handler, { capture: true, once: true })
  }, [disarmGhostClickAfterDrag])

  const { courseChapters = [], published, id, title, deletedAt } = course
  const hasCourseChapters = courseChapters.length > 0
  const courseIri = course['@id']
  const isDeleted = course.isDeleted || !!deletedAt
  const getCoursePreviewReturnTo = (
    selectedThemeId?: number,
    isFinalTest = false
  ) => {
    const params = new URLSearchParams(window.location.search)

    params.set('opened', String(id))

    if (selectedThemeId) {
      params.set('previewThemeId', String(selectedThemeId))
    } else {
      params.delete('previewThemeId')
    }

    const query = params.toString()
    const anchor = isFinalTest
      ? COURSE_PREVIEW_FINAL_TEST_ANCHOR(id)
      : selectedThemeId
      ? COURSE_PREVIEW_THEME_ANCHOR(id, selectedThemeId)
      : COURSE_PREVIEW_ANCHOR(id)

    const returnTo = query ? `${Routes.MY_COURSES}?${query}` : Routes.MY_COURSES

    return `${returnTo}#${anchor}`
  }

  useEffect(() => {
    setLocalChapters([...courseChapters].sort((a, b) => a.sortOrder - b.sortOrder))
    // Initialize local themes for each chapter
    const themesMap = new Map<number, MyCourseChapterTheme[]>()
    courseChapters.forEach((chapter) => {
      const sortedThemes = [...(chapter.courseChapterThemes || [])].sort(
        (a, b) => a.sortOrder - b.sortOrder
      )
      themesMap.set(chapter.id, sortedThemes)
    })
    setLocalThemes(themesMap)
  }, [courseChapters])

  useEffect(() => {
    if (hasRestoredPreviewState.current) {
      return
    }

    if (localThemes.size === 0 && courseChapters.length > 0) {
      return
    }

    if (!previewThemeId) {
      hasRestoredPreviewState.current = true

      return
    }

    const previewThemeIdNumber = Number(previewThemeId)
    if (!Number.isInteger(previewThemeIdNumber) || previewThemeIdNumber <= 0) {
      hasRestoredPreviewState.current = true

      return
    }

    const previewThemeIdString = String(previewThemeIdNumber)
    let foundChapterId: number | null = null

    for (const [chapterId, themes] of localThemes.entries()) {
      const hasTheme = themes.some(
        (theme) => String(theme.id) === previewThemeIdString
      )
      if (hasTheme) {
        foundChapterId = chapterId

        break
      }
    }

    if (foundChapterId === null) {
      return
    }

    setExpandedChapterId(foundChapterId)
    hasRestoredPreviewState.current = true
  }, [courseChapters.length, localThemes, previewThemeId])

  useEffect(() => {
    const hash = window.location.hash

    if (!hash) {
      return
    }

    const anchor = decodeURIComponent(hash.slice(1))
    const courseAnchor = COURSE_PREVIEW_ANCHOR(id)
    const isCoursePreviewAnchor =
      anchor === courseAnchor ||
      anchor === COURSE_PREVIEW_FINAL_TEST_ANCHOR(id) ||
      anchor.startsWith(`${courseAnchor}-theme-`)

    if (!isCoursePreviewAnchor) {
      return
    }

    let animationFrame: number | null = null
    const timeout = setTimeout(() => {
      const themeRow = document.querySelector(`#${CSS.escape(anchor)}`)

      if (!themeRow) {
        return
      }

      themeRow.scrollIntoView({ behavior: 'smooth', block: 'center' })

      animationFrame = requestAnimationFrame(() => {
        const targetRect = themeRow.getBoundingClientRect()
        const targetTop =
          window.scrollY + targetRect.top - (window.innerHeight - targetRect.height) / 2

        if (!Number.isFinite(targetTop)) {
          return
        }

        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth',
        })
      })
    }, PREVIEW_SCROLL_DELAY)

    return () => {
      clearTimeout(timeout)
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [expandedChapterId, hasFinalTest, id, localThemes])

  useEffect(() => {
    toggleLoading(false)
    onChangeSize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    onChangeSize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedChapterId, localChapters.length, hasFinalTest])

  useEffect(() => {
    toggleLoading(false)
    onChangeSize()
  }, [isExpandedLoading, onChangeSize, toggleLoading])

  useEffect(
    () => () => {
      disarmGhostClickAfterDrag()
    },
    [disarmGhostClickAfterDrag]
  )

  useEffect(() => {
    if (deleteChapterState.isSuccess) {
      setDeletingChapterId(null)
      deleteChapterState.reset()
    }
  }, [deleteChapterState])

  useEffect(() => {
    if (deleteChapterState.isError) {
      setDeletingChapterId(null)
      deleteChapterState.reset()
    }
  }, [deleteChapterState])

  useEffect(() => {
    if (deleteThemeState.isSuccess) {
      setLocalThemes((prevThemes) => {
        if (deletingThemeId === null) {
          return prevThemes
        }

        const nextThemes = new Map(prevThemes)
        let hasChanges = false

        for (const [chapterId, themes] of nextThemes.entries()) {
          const filteredThemes = themes.filter((theme) => theme.id !== deletingThemeId)

          if (filteredThemes.length !== themes.length) {
            nextThemes.set(chapterId, filteredThemes)
            hasChanges = true
          }
        }

        return hasChanges ? nextThemes : prevThemes
      })
      setDeletingThemeId(null)
      deleteThemeState.reset()
    }
  }, [deleteThemeState, deletingThemeId])

  useEffect(() => {
    if (deleteThemeState.isError) {
      setDeletingThemeId(null)
      deleteThemeState.reset()
    }
  }, [deleteThemeState])

  useEffect(() => {
    if (patchCourseFinalTestState.isSuccess) {
      notification.success({ message: SUCCESS_DELETE_MESSAGE })
      setIsDeletingFinalTest(false)
      patchCourseFinalTestState.reset()
    }
  }, [patchCourseFinalTestState])

  useEffect(() => {
    if (patchCourseFinalTestState.isError) {
      notification.error({ message: ERROR_DELETE_MESSAGE })
      setIsDeletingFinalTest(false)
      patchCourseFinalTestState.reset()
    }
  }, [patchCourseFinalTestState])

  useEffect(() => {
    if (deleteChapterState.isSuccess || deleteChapterState.isError) {
      setDeletingChapterId(null)
    }
  }, [deleteChapterState.isSuccess, deleteChapterState.isError])

  useEffect(() => {
    if (deleteThemeState.isSuccess || deleteThemeState.isError) {
      setDeletingThemeId(null)
    }
  }, [deleteThemeState.isSuccess, deleteThemeState.isError])

  const handleToggleChapter = (chapterId: number) => {
    setExpandedChapterId((prev) => (prev === chapterId ? null : chapterId))
  }

  const handlePublishToggle = () => {
    publishCourse({ id, published: !published })
  }

  const handleDeleteCourse = () => {
    deleteCourse(id)
  }

  const handleRestoreCourse = () => {
    restoreCourse(id)
  }

  const handleDeleteChapter = async (chapterId: number) => {
    setDeletingChapterId(chapterId)
    await deleteChapter(chapterId)
  }

  const handleEditChapter = (chapter: MyCourseChapter) => {
    setEditChapterData(chapter)
  }

  const handleCloseEditModal = () => {
    setEditChapterData(null)
  }

  const handleChapterDragStart = useCallback(
    (e: DragEvent<HTMLElement>, chapterIndex: number) => {
      disarmGhostClickAfterDrag()
      setDraggedChapterIndex(chapterIndex)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(chapterIndex))
      e.stopPropagation()
    },
    [disarmGhostClickAfterDrag]
  )

  const handleChapterDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    e.stopPropagation()
  }, [])

  const handleChapterDrop = useCallback(
    async (e: DragEvent<HTMLElement>, targetIndex: number) => {
      e.preventDefault()
      e.stopPropagation()

      if (
        draggedChapterIndex === null ||
        draggedChapterIndex === targetIndex
      ) {
        setDraggedChapterIndex(null)
        return
      }

      const newChapters = [...localChapters]
      const [moved] = newChapters.splice(draggedChapterIndex, 1)
      newChapters.splice(targetIndex, 0, moved)

      setLocalChapters(newChapters)
      setDraggedChapterIndex(null)
      const chapterIds = newChapters.map((ch) => ch.id)

      try {
        await reorderChapters({ courseId: id, chapterIds }).unwrap()
      } catch {
        setLocalChapters(localChapters)
      }
    },
    [draggedChapterIndex, localChapters, id, reorderChapters]
  )

  const handleChapterDragEnd = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.stopPropagation()
      setDraggedChapterIndex(null)
      armGhostClickAfterDrag()
    },
    [armGhostClickAfterDrag]
  )

  // Theme handlers
  const handleOpenAddTheme = (chapterId: number) => {
    setAddThemeChapterId(chapterId)
  }

  const handleCloseAddTheme = () => {
    setAddThemeChapterId(null)
  }

  const handleOpenEditTheme = async (theme: MyCourseChapterTheme, chapterId: number) => {
    const result = await getCourseTheme(theme.id)
    if (result.data) {
      setThemeDetailForEdit(result.data)
      setEditThemeData({ chapterId })
    }
  }

  const handleOpenThemePreview = (themeId: number) => {
    navigate(getThemeRoute(themeId, courseIri, 0), {
      state: {
        isPreviewMode: true,
        returnTo: getCoursePreviewReturnTo(themeId),
      },
    })
  }

  const handleOpenFinalTestPreview = () => {
    if (!finalTest?.id) return

    navigate(getThemeRoute(finalTest.id, courseIri, 0), {
      state: {
        isPreviewMode: true,
        returnTo: getCoursePreviewReturnTo(undefined, true),
      },
    })
  }

  const handleCloseEditThemeModal = () => {
    setThemeDetailForEdit(null)
    setEditThemeData(null)
  }

  const handleOpenManualFinalTest = () => {
    setFinalTestForEdit(null)
    toggleManualFinalTestVisible()
  }

  const handleOpenXlsFinalTest = () => {
    setFinalTestForEdit(null)
    toggleXlsFinalTestVisible()
  }

  const handleOpenScormFinalTest = () => {
    setFinalTestForEdit(null)
    toggleScormFinalTestVisible(true)
  }

  const handleCloseManualFinalTestModal = () => {
    setFinalTestForEdit(null)
    setManualFinalTestSuccessVisible(false)
    toggleManualFinalTestVisible(false)
  }

  const handleCloseScormFinalTestModal = () => {
    setFinalTestForEdit(null)
    toggleScormFinalTestVisible(false)
  }

  const handleDeleteFinalTest = async () => {
    if (!hasFinalTest || !finalTest?.id) return

    setIsDeletingFinalTest(true)
    await patchCourseFinalTest({
      id,
      finalTest: null,
    }).unwrap()
  }

  const handleEditFinalTest = async () => {
    if (!finalTest?.id) return

    try {
      const result = await getFinalTestTheme(finalTest.id).unwrap()
      setFinalTestForEdit(result)
      if (result.source === 'scorm') {
        toggleScormFinalTestVisible(true)
        return
      }

      toggleManualFinalTestVisible(true)
    } catch {
      setFinalTestForEdit(null)
      if (finalTest.source === 'scorm') {
        toggleScormFinalTestVisible(true)
      } else {
        toggleManualFinalTestVisible(true)
      }
    }
  }

  const handleDeleteTheme = async (themeId: number) => {
    setDeletingThemeId(themeId)
    await deleteTheme(themeId)
  }

  // Theme drag and drop handlers
  const handleThemeDragStart = useCallback(
    (e: DragEvent<HTMLElement>, chapterId: number, index: number) => {
      disarmGhostClickAfterDrag()
      setDraggedThemeIndex({ chapterId, index })
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
      e.stopPropagation()
    },
    [disarmGhostClickAfterDrag]
  )

  const handleThemeDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    e.stopPropagation()
  }, [])

  const handleThemeDrop = useCallback(
    async (e: DragEvent<HTMLElement>, chapterId: number, dropIndex: number) => {
      e.preventDefault()
      e.stopPropagation()

      if (
        !draggedThemeIndex ||
        draggedThemeIndex.chapterId !== chapterId ||
        draggedThemeIndex.index === dropIndex
      ) {
        setDraggedThemeIndex(null)
        return
      }

      const themes = localThemes.get(chapterId) || []
      const newThemes = [...themes]
      const [removed] = newThemes.splice(draggedThemeIndex.index, 1)
      newThemes.splice(dropIndex, 0, removed)

      const newLocalThemes = new Map(localThemes)
      newLocalThemes.set(chapterId, newThemes)
      setLocalThemes(newLocalThemes)
      setDraggedThemeIndex(null)

      const themeIds = newThemes.map((th) => th.id)
      try {
        await reorderThemes({ chapterId, themeIds }).unwrap()
      } catch {
        setLocalThemes((prev) => {
          const next = new Map(prev)
          next.set(chapterId, themes)
          return next
        })
      }
    },
    [draggedThemeIndex, localThemes, reorderThemes]
  )

  const handleThemeDragEnd = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.stopPropagation()
      setDraggedThemeIndex(null)
      armGhostClickAfterDrag()
    },
    [armGhostClickAfterDrag]
  )

  const renderChapter = (chapter: MyCourseChapter, chapterIndex: number) => {
    const isExpanded = expandedChapterId === chapter.id
    const themes = localThemes.get(chapter.id) || []
    return (
      <ChapterWrapper
        key={chapter.id}
        isDragging={draggedChapterIndex === chapterIndex}
      >
        <ChapterHeader
          expanded={isExpanded}
          isEditable={isEditable}
          onClick={() => handleToggleChapter(chapter.id)}
          onDragOver={handleChapterDragOver}
          onDrop={(e: DragEvent<HTMLElement>) => {
            handleChapterDrop(e, chapterIndex)
          }}
        >
          <ChapterLeftContent>
            {isEditable && (
              <ChapterDragHandle
                draggable
                onDragStart={(e: DragEvent<HTMLElement>) =>
                  handleChapterDragStart(e, chapterIndex)
                }
                onDragEnd={handleChapterDragEnd}
                onClick={(e: ReactMouseEvent<HTMLElement>) => e.stopPropagation()}
                title="Перетащите для изменения порядка"
              >
                <MenuOutlined />
              </ChapterDragHandle>
            )}
            <ChapterTitle $expanded={isExpanded}>{chapter.title}</ChapterTitle>
          </ChapterLeftContent>
          <ChapterActions>
            {isEditable && (
              <>
                <Link
                  text="Изменить"
                  linkStyle={LinkStyle.DEFAULT}
                  onClick={(e: { stopPropagation: () => void }) => {
                    e.stopPropagation()
                    handleEditChapter(chapter)
                  }}
                  fontSize={10}
                />
                <DeleteButton
                  {...(deletingChapterId === chapter.id ? deleteChapterState : {
                    isSuccess: false,
                    isError: false,
                    isLoading: false,
                    reset: undefined,
                  })}
                  isLoading={deletingChapterId === chapter.id && deleteChapterState.isLoading}
                  onClick={() => handleDeleteChapter(chapter.id)}
                  deleteDescription={`Вы точно хотите удалить раздел: ${chapter.title}?`}
                  isLink
                />
              </>
            )}
            <ChapterArrow expanded={isExpanded}>&#9660;</ChapterArrow>
          </ChapterActions>
        </ChapterHeader>
        {isExpanded && (
          <ThemesWrapper
            onDragOver={handleThemeDragOver}
            onDrop={(e: DragEvent<HTMLElement>) => {
              e.preventDefault()
              e.stopPropagation()
              if (!draggedThemeIndex) {
                return
              }

              handleThemeDrop(
                e as DragEvent<HTMLElement>,
                chapter.id,
                themes.length
              )
            }}
          >
            <ThemesTitle>Темы</ThemesTitle>
            {themes.map((theme, themeIndex) => {
              const isThemeDragging =
                draggedThemeIndex?.chapterId === chapter.id &&
                draggedThemeIndex?.index === themeIndex

              return (
                <ThemeItem
                  key={theme.id}
                  id={COURSE_PREVIEW_THEME_ANCHOR(id, theme.id)}
                  isDragging={isThemeDragging}
                  onDragOver={handleThemeDragOver}
                  onDrop={(e: DragEvent<HTMLElement>) => {
                    e.stopPropagation()
                    handleThemeDrop(e, chapter.id, themeIndex)
                  }}
                >
                  {isEditable && (
                    <ThemeDragHandle
                      draggable
                      onDragStart={(e: DragEvent<HTMLSpanElement>) => {
                        handleThemeDragStart(e, chapter.id, themeIndex)
                      }}
                      onDragEnd={handleThemeDragEnd}
                      onClick={(e: ReactMouseEvent<HTMLElement>) => e.stopPropagation()}
                      title="Перетащите для изменения порядка"
                    >
                      <MenuOutlined />
                    </ThemeDragHandle>
                  )}
                  <ThemeTitleGroup>
                    <ThemeTitle onClick={() => handleOpenThemePreview(theme.id)}>
                      {theme.title}
                    </ThemeTitle>
                    {isEditable && (
                      <ThemeActions>
                        <Link
                          text="Изменить"
                          linkStyle={LinkStyle.DEFAULT}
                          onClick={(e: { stopPropagation: () => void }) => {
                            e.stopPropagation()
                            handleOpenEditTheme(theme, chapter.id)
                          }}
                          fontSize={10}
                        />
                        <DeleteButton
                          {...(deletingThemeId === theme.id ? deleteThemeState : {
                            isSuccess: false,
                            isError: false,
                            isLoading: false,
                            reset: undefined,
                          })}
                          isLoading={deletingThemeId === theme.id && deleteThemeState.isLoading}
                          onClick={() => handleDeleteTheme(theme.id)}
                          deleteDescription={`Удалить тему: ${theme.title}?`}
                          isLink
                        />
                      </ThemeActions>
                    )}
                  </ThemeTitleGroup>
                </ThemeItem>
              )
            })}
            {themes.length === 0 && (
              <ThemeItem>
                <ThemeTitle style={{ fontStyle: 'italic', color: '#999' }}>
                  Нет тем
                </ThemeTitle>
              </ThemeItem>
            )}
            {isEditable && (
              <AddThemeButton
                type="button"
                onClick={() => handleOpenAddTheme(chapter.id)}
              >
                <PlusOutlined /> Добавить тему
              </AddThemeButton>
            )}
          </ThemesWrapper>
        )}
      </ChapterWrapper>
    )
  }

  const renderFinalTestRow = () => {
    if (!hasFinalTest || !finalTest) return null

    return (
      <FinalTestRow id={COURSE_PREVIEW_FINAL_TEST_ANCHOR(id)}>
        <FinalTestLeftContent>
          <ChapterDragHandle as="span">
            <MenuOutlined />
          </ChapterDragHandle>
          <FinalTestTitleGroup onClick={handleOpenFinalTestPreview}>
            <ChapterTitle $expanded={false}>Итоговое тестирование</ChapterTitle>
          </FinalTestTitleGroup>
        </FinalTestLeftContent>
        <ChapterActions>
          {isEditable && (
            <>
              <Link
                text="Изменить"
                linkStyle={LinkStyle.DEFAULT}
                onClick={handleEditFinalTest}
                fontSize={10}
              />
              <DeleteButton
                isLoading={isDeletingFinalTest && patchCourseFinalTestState.isLoading}
                onClick={handleDeleteFinalTest}
                deleteDescription="Вы уверены, что хотите удалить итоговое тестирование?"
                isLink
              />
            </>
          )}
          <ChapterArrow expanded={false}>&#9660;</ChapterArrow>
        </ChapterActions>
      </FinalTestRow>
    )
  }

  const PublishComponent = published ? CloseButton : DoneButton
  const publishText = published ? 'Снять с публикации' : 'Опубликовать'
  const shouldRenderChapters =
    localChapters.length > 0 || hasCourseChapters || isEditable

  return (
    <BodyWrapper data-deleted={isDeleted}>
      <BodyLoadingOverlay isVisible={isExpandedLoading}>
        <Loader isLoading={isExpandedLoading} />
      </BodyLoadingOverlay>
      {shouldRenderChapters ? (
        <>
          <SectionHeader>
            <SectionTitle mySize="h4">РАЗДЕЛЫ</SectionTitle>
          </SectionHeader>
          {localChapters.length > 0 ? (
            localChapters.map((chapter, idx) => renderChapter(chapter, idx))
          ) : (
            !isExpandedLoading &&
            !hasCourseChapters && (
              <NoChaptersMessage>В курсе пока нет разделов и тем</NoChaptersMessage>
            )
          )}
          {renderFinalTestRow()}
        </>
      ) : (
        <>
          {!isExpandedLoading && !hasCourseChapters && (
            <NoChaptersMessage>В курсе пока нет разделов и тем</NoChaptersMessage>
          )}
          {renderFinalTestRow()}
        </>
      )}

      <ActionsWrapper>
        {isDeleted && showDeleted && deletedAt && (
          <div style={{ color: '#999', marginRight: '16px' }}>
            Удалён: {new Date(deletedAt).toLocaleString('ru-RU')}
          </div>
        )}
        {onEdit && !isDeleted && (
          <StyledButton
            id={`my-course-edit-button-${id}`}
            buttonStyle={ButtonStyle.GRAY}
            leftIcon={<EditOutlined />}
            onClick={onEdit}
          >
            Редактировать
          </StyledButton>
        )}
        {isEditable && (
          <AddButton
            id={`my-course-add-section-button-${id}`}
            onClick={toggleAddChapter}
          >
            Добавить раздел
          </AddButton>
        )}
        {!isDeleted && isEditable && !hasFinalTest && (
          <AddFinalTestButton
            courseId={id}
            courseIri={courseIri}
            onManualCreate={handleOpenManualFinalTest}
            onXlsCreate={handleOpenXlsFinalTest}
            onScormCreate={handleOpenScormFinalTest}
          />
        )}
        {!isDeleted && (
          <PublishComponent
            id={`my-course-publish-button-${id}`}
            isLoading={isPublishing}
            onClick={handlePublishToggle}
          >
            {publishText}
          </PublishComponent>
        )}
        {!isDeleted && (
          <DeleteButton
            {...deleteState}
            onClick={handleDeleteCourse}
            deleteDescription={
              published
                ? `Вы уверены, что хотите удалить курс "${title}"? Внимание: курс опубликован!`
                : `Вы уверены, что хотите удалить курс "${title}"?`
            }
          />
        )}
        {isDeleted && showDeleted && isEditable && (
          <StyledButton
            buttonStyle={ButtonStyle.PRIMARY}
            onClick={handleRestoreCourse}
            disabled={restoreState.isLoading}
          >
            Восстановить
          </StyledButton>
        )}
      </ActionsWrapper>

      {/* Add Chapter Modal */}
      <Modal
        visible={addChapterVisible}
        onModalClose={toggleAddChapter}
        withoutCloseOnOutsideClick
      >
        <ChapterFormModal
          isEdit={false}
          courseIri={courseIri}
          handleCloseModal={toggleAddChapter}
        />
      </Modal>

        {/* Edit Chapter Modal */}
        <Modal
          visible={!!editChapterData}
          onModalClose={handleCloseEditModal}
          withoutCloseOnOutsideClick
        >
          {editChapterData && (
            <ChapterFormModal
              isEdit
              courseIri={courseIri}
              handleCloseModal={handleCloseEditModal}
              chapter={{
                ...editChapterData,
                themesCount: editChapterData.courseChapterThemes?.length || 0,
                value: editChapterData['@id'],
                label: editChapterData.title,
              }}
            />
          )}
        </Modal>

        {/* Add Theme Modal */}
        <Modal
          visible={addThemeChapterId !== null}
          onModalClose={handleCloseAddTheme}
          withoutCloseOnOutsideClick
        >
          {addThemeChapterId !== null && (
            <ThemeFormModal
              isEdit={false}
              chapterIri={localChapters.find((ch) => ch.id === addThemeChapterId)?.['@id'] || ''}
              courseIri={courseIri}
              handleCloseModal={handleCloseAddTheme}
              internalCourseMode
            />
          )}
        </Modal>

        <Modal
          visible={isManualFinalTestVisible}
          onModalClose={handleCloseManualFinalTestModal}
          {...manualFinalTestModalProps}
        >
          <ManualFinalTestModal
            courseId={id}
            handleCloseModal={handleCloseManualFinalTestModal}
            isEdit={Boolean((finalTest || finalTestForEdit) && finalTestSource !== 'scorm')}
            finalTest={finalTestForEdit || undefined}
            onSuccessVisibleChange={setManualFinalTestSuccessVisible}
          />
        </Modal>

        <Modal
          visible={isScormFinalTestVisible}
          onModalClose={handleCloseScormFinalTestModal}
        >
          <ScormFinalTestModal
            courseId={id}
            courseIri={courseIri}
            handleCloseModal={handleCloseScormFinalTestModal}
            isEdit={Boolean(finalTest && finalTestSource === 'scorm')}
            finalTestId={finalTestForEdit?.id || finalTest?.id}
            initialHoursTime={finalTestForEdit?.hoursTime || finalTest?.hoursTime}
            initialFile={finalTestForEdit?.courseTheme?.file}
          />
        </Modal>

        <Modal visible={isXlsFinalTestVisible} onModalClose={toggleXlsFinalTestVisible}>
          <XlsFinalTestModal
            courseId={id}
            handleCloseModal={toggleXlsFinalTestVisible}
          />
        </Modal>

        {/* Edit Theme Modal */}
        <Modal
          visible={editThemeData !== null && themeDetailForEdit !== null}
          onModalClose={handleCloseEditThemeModal}
          withoutCloseOnOutsideClick
        >
          {editThemeData && themeDetailForEdit && (
            <ThemeFormModal
              isEdit
              chapterIri={localChapters.find((ch) => ch.id === editThemeData.chapterId)?.['@id'] || ''}
              courseIri={courseIri}
              handleCloseModal={handleCloseEditThemeModal}
              internalCourseMode
              internalTheme={{
                id: themeDetailForEdit.id,
                title: themeDetailForEdit.title,
                test: themeDetailForEdit.test,
                hoursTime: themeDetailForEdit.hoursTime,
                sortOrder: 0,
                controlQuestion: themeDetailForEdit.controlQuestion,
                '@id': themeDetailForEdit['@id'],
                '@type': themeDetailForEdit['@type'],
                courseTheme: themeDetailForEdit.courseTheme
                  ? {
                      id: String(themeDetailForEdit.courseTheme.id),
                      type: themeDetailForEdit.courseTheme.type,
                      '@id': themeDetailForEdit.courseTheme['@id'],
                      '@type': themeDetailForEdit.courseTheme['@type'],
                    }
                  : undefined,
              }}
            />
          )}
        </Modal>

        {canSeeOnboarding &&
          isEditable &&
          !isDeleted &&
          !published &&
          !hasFinalTest &&
          onEdit && (
            <Onboarding
              steps={getOnboardingSteps(id)}
              firstStepTitle={ONBOARDING_TITLE}
              code={OnboardingCodes.my_courses_open_card}
            />
          )}
    </BodyWrapper>
  )
}
/* eslint-enable sonarjs/cognitive-complexity */

export default Body
